import React from "react";
import produce from "immer";
import moment from "moment";
import styled, { css } from "styled-components";
import { useMutation, queryCache } from "react-query";
import {
  Button,
  Row,
  Col,
  Checkbox,
  Tooltip,
  Avatar,
  Popconfirm,
  Typography,
  Spin,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  MoreOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import EditTaskModal from "components/modal/EditTaskModal";
import { getInitials } from "lib/typography";
import { calculateDayDiffFromToday } from "lib/date";
import CreateTasks from "components/job/index/todo/createTasks";

const { Text } = Typography;

const deleteTask = async ({ todoId, taskId }) => {
  try {
    const res = await apiAxios.delete(`/todos/${todoId}/tasks/${taskId}`);
    return res.status;
  } catch (e) {
    throw e.response.data.message;
  }
};

const markDoneTask = async ({ todoId, taskId }) => {
  try {
    const res = await apiAxios.put(`/todos/${todoId}/tasks/${taskId}`, {
      is_done: true,
      done_at: moment().format(),
    });
    return res.data.data.is_done;
  } catch (e) {
    throw e.response.data.message;
  }
};

const markUnDoneTask = async ({ todoId, taskId }) => {
  try {
    const res = await apiAxios.put(`/todos/${todoId}/tasks/${taskId}`, {
      is_done: false,
      done_at: null,
    });
    return res.data.data.is_done;
  } catch (e) {
    throw e.response.data.message;
  }
};

const TasksList = (props) => {
  // Component Props
  const { tasks, status, jobId, todoId } = props;

  // Component State
  const [modalVisible, setModalVisible] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState({});
  const [error, setError] = React.useState("");
  const [showCreateTask, setShowCreateTask] = React.useState(false);

  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  // Component Function
  const confirmDeleteTask = async (taskId) => {
    setLoading(`deleteTask-${taskId}`);
    try {
      await mutateDelete({ taskId, jobId, todoId });
      finishLoading(`deleteTask-${taskId}`);
      setSuccessMessage("Succesfully deleted task");
    } catch (error) {
      finishLoading(`deleteTask-${taskId}`);
      console.log(error);
    }
  };

  const handleOpenModal = (task) => {
    setSelectedTask(task);
    setModalVisible(true);
  };

  const handleTaskCheckbox = async (value, todoId, taskId) => {
    if (value == true) {
      setLoading(`markTask-${taskId}`);
      try {
        await mutateDoneTask({ todoId, taskId, jobId });
        finishLoading(`markTask-${taskId}`);
      } catch (error) {
        finishLoading(`markTask-${taskId}`);
        console.log(error);
      }
    } else if (value === false) {
      setLoading(`markTask-${taskId}`);
      try {
        await mutateUnDoneTask({ todoId, taskId, jobId });
        finishLoading(`markTask-${taskId}`);
      } catch (error) {
        finishLoading(`markTask-${taskId}`);
        console.log(error);
      }
    }
  };

  // Mutation
  const [mutateDelete] = useMutation(deleteTask, {
    onSuccess: (_, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const updatedTasks = produce(oldData, (draft) => {
            draft.todo.tasks = draft.todo.tasks.filter(
              (task) => task.id !== variables.taskId
            );
          });

          return updatedTasks;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  const [mutateDoneTask] = useMutation(markDoneTask, {
    onSuccess: (isDone, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const taskIndex = oldData.todo.tasks.findIndex(
            (task) => task.id === variables.taskId
          );

          const updatedTask = produce(oldData, (draft) => {
            draft.todo.tasks[taskIndex].is_done = isDone;
          });

          return updatedTask;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  const [mutateUnDoneTask] = useMutation(markUnDoneTask, {
    onSuccess: (isDone, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const taskIndex = oldData.todo.tasks.findIndex(
            (task) => task.id === variables.taskId
          );
          const updatedTask = produce(oldData, (draft) => {
            draft.todo.tasks[taskIndex].is_done = isDone;
          });

          return updatedTask;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  return (
    <>
      {status !== "loading" ? (
        <>
          <StyledChildRow>
            <Col span={24}>
              {tasks?.map((task) => {
                const dayDiff = calculateDayDiffFromToday(
                  moment(task.due_date)
                );
                return (
                  <StyledTasksList key={task.id}>
                    <Row style={{ width: "100%" }} align="middle">
                      <Col xs={14}>
                        <Checkbox
                          checked={task.is_done}
                          onChange={(e) =>
                            handleTaskCheckbox(
                              e.target.checked,
                              task.todo_id,
                              task.id
                            )
                          }
                        >
                          {notificationState.loading[`markTask-${task.id}`] && (
                            <Spin size="small" style={{ marginRight: "5px" }} />
                          )}
                          <Text>{task.title}</Text>
                        </Checkbox>
                      </Col>

                      <Col xs={10}>
                        <div
                          css={`
                            display: flex;
                            justify-content: flex-end;
                            align-items: center;
                          `}
                        >
                          {task.due_date && (
                            <StyledDueDateContainer
                              variant={
                                dayDiff < 10
                                  ? "danger"
                                  : dayDiff <= 20
                                  ? "warning"
                                  : "good"
                              }
                            >
                              <BellOutlined className="icon" />
                              <Text className="text">
                                {moment(task.due_date).utc().format("DD MMMM")}
                              </Text>
                            </StyledDueDateContainer>
                          )}

                          {task.asignees?.map((asignee) => {
                            const initialName = getInitials(asignee.name);
                            return (
                              <Tooltip title={asignee.name} key={asignee.id}>
                                <Avatar
                                  size="small"
                                  style={{
                                    marginRight: "5px",
                                    color: "#f56a00",
                                    backgroundColor: " #fde3cf",
                                  }}
                                >
                                  {initialName}
                                </Avatar>
                              </Tooltip>
                            );
                          })}
                          <Popconfirm
                            title="Are you sure delete this task?"
                            onConfirm={() => confirmDeleteTask(task.id)}
                            onCancel={() => {
                              return;
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="link"
                              size="small"
                              icon={<DeleteOutlined />}
                              loading={
                                notificationState.loading[
                                  `deleteTask-${task.id}`
                                ]
                              }
                              css={`
                                color: ${(props) =>
                                  props.theme.colors.grey[700]};
                                margin-right: ${(props) =>
                                  props.theme.spacing[1]};
                              `}
                            />
                          </Popconfirm>
                          <Button
                            type="link"
                            size="small"
                            icon={<MoreOutlined />}
                            onClick={() => handleOpenModal(task)}
                            css={`
                              color: ${(props) => props.theme.colors.grey[700]};
                            `}
                          />
                        </div>
                      </Col>
                    </Row>
                  </StyledTasksList>
                );
              })}
            </Col>

            {showCreateTask ? (
              <Col xs={24}>
                <CreateTasks
                  jobId={jobId}
                  todoId={todoId}
                  setShowCreateTask={setShowCreateTask}
                />
              </Col>
            ) : (
              <Col span={24}>
                <StyledTasksList $add onClick={() => setShowCreateTask(true)}>
                  <StyledButton
                    shape="circle"
                    size="small"
                    icon={<PlusOutlined />}
                  />
                  <ShallowText>Add todo</ShallowText>
                </StyledTasksList>
              </Col>
            )}
          </StyledChildRow>
          <EditTaskModal
            modalVisible={modalVisible}
            selectedTask={selectedTask}
            jobId={jobId}
            todoId={todoId}
            handleCloseModal={() => {
              setModalVisible(false);
            }}
          />
        </>
      ) : (
        <span>Hello</span>
      )}
    </>
  );
};

const StyledChildRow = styled(Row)`
  background-color: ${(props) => props.theme.colors.white};
  margin-top: ${(props) => props.theme.spacing[2]};
  ${(props) =>
    props.$newJob &&
    css`
      padding: ${(props) => props.theme.spacing[4]};
    `};
`;

const StyledTasksList = styled.div`
  padding: ${(props) => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
  ${(props) =>
    props.$add &&
    css`
      border-bottom: none;
      cursor: pointer;
    `};
  &:hover {
    background: ${(props) => props.theme.colors.grey[100]};
  }
`;

const StyledButton = styled(Button)`
  background: ${(props) => props.theme.colors.grey[300]};
  color: ${(props) => props.theme.colors.grey[600]};
  margin-right: ${(props) => props.theme.spacing[2]};
`;

const StyledDueDateContainer = styled.div`
  border-radius: ${(props) => props.theme.spacing[1]};
  display: flex;
  align-items: center;
  padding: 1px;
  margin-right: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) =>
    props.variant === "good"
      ? props.theme.colors.green[200]
      : props.variant === "warning"
      ? props.theme.colors.yellow[200]
      : props.variant === "danger" && props.theme.colors.red[200]};
  border: 1px solid
    ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[400]
        : props.variant === "warning"
        ? props.theme.colors.yellow[400]
        : props.variant === "danger" && props.theme.colors.red[400]};
  .text {
    margin-right: 5px;
    font-size: 0.7rem;
    color: ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[700]
        : props.variant === "warning"
        ? props.theme.colors.yellow[700]
        : props.variant === "danger" && props.theme.colors.red[700]};
  }
  .icon {
    margin: 0 5px;
    color: ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[700]
        : props.variant === "warning"
        ? props.theme.colors.yellow[700]
        : props.variant === "danger" && props.theme.colors.red[700]};
  }
`;

const ShallowText = styled(Text)`
  color: ${(props) => props.theme.colors.grey[500]};
  font-size: ${(props) => props.theme.fontSize["sm"]};
`;

export default TasksList;
